import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, LockOpen, CaretLeft, CaretRight, ShieldCheck } from "@phosphor-icons/react";

function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function defaultMonth() {
  const today = new Date();
  // Close happens after month end — default to the previous calendar month.
  const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(monthStr) {
  const [y, m] = monthStr.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

/**
 * Simplified Close page (2026-06): just a Closed/Open toggle per month.
 * No checklist, no sign-off ceremony, no sub-tasks. The previous version had
 * a full checklist with metric panels — owner asked for it to be stripped
 * down to a binary: "Is this month closed? Yes/No." Soft lock — closing a
 * month doesn't prevent any edits, it's purely a visual marker.
 */
export default function Close() {
  const { activeCompanyId, activeCompany } = useCompany();
  const { user } = useAuth();
  const [month, setMonth] = useState(defaultMonth());
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/close?company_id=${activeCompanyId}&month=${month}`);
      setPeriod(data);
    } catch (e) {
      // Backend returns 404 when there's no record yet — treat as "open".
      if (e?.response?.status === 404) setPeriod({ status: "open" });
      else toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, month]);

  useEffect(() => { load(); }, [load]);

  const close = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/close/sign-off", { company_id: activeCompanyId, month });
      setPeriod(data);
      toast.success(`${monthLabel(month)} closed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close");
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/close/reopen", { company_id: activeCompanyId, month });
      setPeriod(data);
      toast.success(`${monthLabel(month)} reopened`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reopen");
    } finally {
      setSaving(false);
    }
  };

  const isClosed = period?.status === "closed";
  // Owner + bookkeeper can both close and reopen. Staff (Tessina) sees
  // read-only — she's the one uploading docs, not driving the close.
  const canToggle = user?.role === "owner" || user?.role === "bookkeeper";

  return (
    <div className="p-6 sm:p-8 max-w-3xl" data-testid="close-page">
      <div className="mb-8">
        <div className="label-tag mb-2">Month-end Close</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
          Is this month closed?
        </h1>
        <p className="text-[#525252] mt-3">
          {activeCompany?.name}. One click marks a month as closed for the bookkeeper.
          {!canToggle && " You can see the status here; the owner or bookkeeper changes it."}
        </p>
      </div>

      {/* Month picker */}
      <div className="flex items-center gap-2 mb-6" data-testid="close-month-picker">
        <button
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
          className="p-2 border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
          aria-label="Previous month"
          data-testid="close-month-prev"
        >
          <CaretLeft size={16} weight="bold" />
        </button>
        <div className="px-4 py-2 border border-[#0A0A0A] bg-[#0A0A0A] text-white text-sm font-medium min-w-[180px] text-center" data-testid="close-month-label">
          {monthLabel(month)}
        </div>
        <button
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
          className="p-2 border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
          aria-label="Next month"
          data-testid="close-month-next"
        >
          <CaretRight size={16} weight="bold" />
        </button>
        <button
          onClick={() => setMonth(defaultMonth())}
          className="ml-2 text-xs text-[#525252] hover:text-[#0A0A0A] underline"
          data-testid="close-month-default"
        >
          Last month
        </button>
      </div>

      {loading ? (
        <div className="surface-card bg-white p-8 text-center text-sm text-[#525252]">Loading…</div>
      ) : (
        <div
          className={`surface-card p-8 ${isClosed ? "bg-[#10B981]/[0.06] border-l-4 border-[#10B981]" : "bg-white"}`}
          data-testid="close-status-card"
        >
          {/* Big visual status */}
          <div className="flex items-center gap-4 mb-6">
            {isClosed ? (
              <Lock size={48} weight="duotone" className="text-[#10B981] flex-shrink-0" />
            ) : (
              <LockOpen size={48} weight="duotone" className="text-[#F59E0B] flex-shrink-0" />
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#525252] mb-1">
                {monthLabel(month)}
              </div>
              <div className="font-heading text-3xl font-medium" data-testid="close-status-text">
                {isClosed ? "Closed" : "Open"}
              </div>
              {isClosed && period?.closed_at && (
                <div className="text-xs text-[#525252] mt-1">
                  Closed by <b>{period.closed_by_name || "—"}</b> on {fmtDateTime(period.closed_at)}
                </div>
              )}
              {!isClosed && period?.reopened_at && (
                <div className="text-xs text-[#525252] mt-1">
                  Reopened by <b>{period.reopened_by_name || "—"}</b> on {fmtDateTime(period.reopened_at)}
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          {canToggle ? (
            <div className="flex gap-3 flex-wrap">
              {!isClosed ? (
                <button
                  onClick={close}
                  disabled={saving}
                  className="px-6 py-3 bg-[#10B981] text-white hover:bg-[#0EA572] disabled:opacity-50 flex items-center gap-2 font-semibold"
                  data-testid="close-mark-closed"
                >
                  <Lock size={18} weight="bold" />
                  Mark {monthLabel(month)} as Closed
                </button>
              ) : (
                <button
                  onClick={reopen}
                  disabled={saving}
                  className="px-6 py-3 bg-[#F59E0B] text-white hover:bg-[#D97706] disabled:opacity-50 flex items-center gap-2 font-semibold"
                  data-testid="close-mark-open"
                >
                  <LockOpen size={18} weight="bold" />
                  Reopen {monthLabel(month)}
                </button>
              )}
            </div>
          ) : (
            <div className="text-sm text-[#525252] italic">
              Read-only — owner or bookkeeper changes the close status.
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-[#E5E5E5] flex items-start gap-2 text-xs text-[#525252]">
            <ShieldCheck size={14} weight="duotone" className="text-[#525252] mt-0.5 flex-shrink-0" />
            <span>
              Soft lock — closing a month is just a visual marker for you and your bookkeeper.
              Nothing is prevented from being edited.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
